import { TableStateWithFilters } from "@king-ict/eupisi-ui/components";
import { api } from "@king-ict/eupisi-ui/utils";

import BenefitDetailsFetchingParameters from "../models/Benefit/BenefitDetailsFetchingParameters";
import BenefitIntegrationPersonData from "../models/Benefit/BenefitIntegrationPersonData";
import InstitutionBenefit from "../models/Benefit/InstitutionBenefit";
import CriterionFetchingParameters from "../models/Criterion/CriterionFetchingParameters";
import CriterionIntegrationData from "../models/Criterion/CriterionIntegrationData";
import EvaluatedCriterion from "../models/Criterion/EvaluatedCriterion";
import InstitutionCriterion from "../models/Criterion/InstitutionCriterion";
import { EnrollmentCyclePedagogicalYear } from "../models/EnrollmentCycle/EnrollmentCyclePedagogicalYear";
import ImportantDate from "../models/ImportantDate/ImportantDate";
import RegularTerm from "../models/ImportantDate/RegularTerm";
import InstitutionDetails from "../models/Institution/InstitutionDetails";
import InstitutionName from "../models/Institution/InstitutionName";
import InstitutionProgram from "../models/Institution/InstitutionProgram";
import InstitutionProgramListItem from "../models/Institution/InstitutionProgramListItem";
import {
  InstitutionSearchItem,
  InstitutionFilters,
} from "../models/Institution/InstitutionSearch";
import LandingPageMessage from "../models/LandingPageMessage/LandingPageMessage";
import MunicipalityWithCounty from "../models/Municipality/MunicipalityWithCounty";
import PedagogicalYear from "../models/PedagogicalYear/PedagogicalYear";
import ApplicantParent from "../models/Person/ApplicantParent";
import Child from "../models/Person/Child";
import OtherParent from "../models/Person/OtherParent";
import Person from "../models/Person/Person";
import PhysicalExaminationData from "../models/PhysicalExamination/PhysicalExaminationData";
import Questionnaire from "../models/Questionnaire/Questionnaire";
import InitialConversationQuestionGroup from "../models/Request/InitialConversationQuestionGroup";
import Request from "../models/Request/Request";
import RequestBasicInfo from "../models/Request/RequestBasicInfo";
import RequestBenefitData from "../models/Request/RequestBenefitData";
import RequestComment from "../models/Request/RequestComment";
import RequestCriterionData from "../models/Request/RequestCriterionData";
import { RequestDocumentData } from "../models/Request/RequestDocumentData";
import RequestHistoryItem from "../models/Request/RequestHistoryItem";
import RequestStatus from "../models/Request/RequestStatus";
import CodeBook from "../models/Shared/CodeBook";
import PagedResult from "../models/Shared/PagedResult";
import Town from "../models/Town/Town";
import { IRoleChangeItem } from "../models/User";

const countiesUrlPrefix: string = "counties";
const eventsUrlPrefix: string = "events";
const institutionsUrlPrefix: string = "institutions";
const municipalitiesUrlPrefix: string = "municipalities";
const nationalitiesUrlPrefix: string = "nationalities";
const nationalMinoritiesUrlPrefix: string = "national-minorities";
const parentNumbersUrlPrefix: string = "parent-numbers";
const parentshipTypesUrlPrefix: string = "parentship-types";
const pedagogicalYearsUrlPrefix: string = "pedagogical-years";
const personsUrlPrefix: string = "persons";
const professionalQualificationsUrlPrefix: string =
  "professional-qualifications";
const professionsUrlPrefix: string = "professions";
const programAgesUrlPrefix: string = "program-ages";
const programDurationsUrlPrefix: string = "program-durations";
const programsUrlPrefix: string = "programs";
const programTypesUrlPrefix: string = "program-types";
const questionnairesUrlPrefix: string = "questionnaires";
const requestsUrlPrefix: string = "requests";
const requestStatusesUrlPrefix: string = "request-statuses";
const townsUrlPrefix: string = "towns";
const documentsUrlPrefix: string = "documents";
const userUrlPrefix = "users";
const criteriaUrlPrefix = "criteria";
const benefitsUrlPrefix = "benefits";
const landingPageMessageUrlPrefix = "landing-page-message";
const monthsUrlPrefix: string = "months";
const relationshipsUrlPrefix: string = "relationships";
const documentTypesUrlPrefix: string = "document-types";
const getReasonNonExistenceOibTypesUrlPrefix: string =
  "reason-nonexistence-oib";
const sexesUrlPrefix: string = "sexes";
const enrollmentCyclesUrlPrefix: string = "enrollment-cycles";

export const getCurrentImportantDate = async () => {
  return api.get(
    `${eventsUrlPrefix}/important-dates/current`
  ) as Promise<ImportantDate>;
};

export const getRegularTerm = async () => {
  return api.get(
    `${eventsUrlPrefix}/regular-term/current`
  ) as Promise<RegularTerm>;
};

export const getCurrentPedagogicalYear = async () => {
  return api.get(
    `${pedagogicalYearsUrlPrefix}/current`
  ) as Promise<PedagogicalYear>;
};

export const getPerson = async (oib: string) => {
  return api.get(`${personsUrlPrefix}/${oib}`) as Promise<Person>;
};

export const getPersonChildren = async () => {
  return api.get(`${personsUrlPrefix}/children`) as Promise<Person[]>;
};

export const getChildSiblings = async (
  childOib: string,
  applicantParentOib: string,
  otherParentOib?: string
) => {
  return api.get(`${personsUrlPrefix}/${childOib}/siblings`, {
    applicantParentOib: applicantParentOib,
    otherParentOib: otherParentOib,
  }) as Promise<Person[]>;
};

export const getApplicantParent = async (applicantParentOib?: string) => {
  return api.get(`${personsUrlPrefix}/applicant-parents`, {
    applicantParentOib,
  }) as Promise<ApplicantParent>;
};

export const getChild = async (oib: string) => {
  return api.get(`${personsUrlPrefix}/children/${oib}`) as Promise<Child>;
};

export const getApplicantParentByRequestId = async (
  requestId: number,
  refreshData?: boolean
) => {
  return api.get(`${requestsUrlPrefix}/${requestId}/applicant-parent`, {
    refreshData: refreshData,
  }) as Promise<ApplicantParent>;
};

export const getRequestBasicInfo = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/basic-info`
  ) as Promise<RequestBasicInfo>;
};

export const getOtherParentByRequestId = async (
  requestId: number,
  refreshData?: boolean
) => {
  return api.get(`${requestsUrlPrefix}/${requestId}/other-parent`, {
    refreshData: refreshData,
  }) as Promise<OtherParent>;
};

export const getChildByRequestId = async (
  requestId: number,
  refreshData?: boolean
) => {
  return api.get(`${requestsUrlPrefix}/${requestId}/child`, {
    refreshData: refreshData,
  }) as Promise<Child>;
};

export const getInstitutionByRequestId = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/institution`
  ) as Promise<InstitutionDetails>;
};

export const getInstitutionNameByRequestId = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/institution`
  ) as Promise<InstitutionName>;
};

export const getLocationProgramsByRequestId = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/location-programs`
  ) as Promise<InstitutionProgram[]>;
};

export const getRequestCriterionDataByRequestId = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/criteria-data`
  ) as Promise<RequestCriterionData>;
};

export const getInitialConversationByRequestId = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/initial-conversation`
  ) as Promise<InitialConversationQuestionGroup[]>;
};

export const getUnlockReasonsByRequestId = async (requestId: number) => {
  return api.get(`${requestsUrlPrefix}/${requestId}/unlock-reasons`) as Promise<
    RequestComment[]
  >;
};

export const getRequestStatuses = async () => {
  return api.get(requestStatusesUrlPrefix) as Promise<RequestStatus[]>;
};

export const getDraftRequest = async (guid: string, refreshData?: boolean) => {
  return api.get(`${requestsUrlPrefix}/drafts/${guid}`, {
    refreshData: refreshData,
  }) as Promise<Request>;
};

export const getExistingInitialConversation = async (
  applicantParentOib: string,
  childOib: string
) => {
  return api.get(`${requestsUrlPrefix}/existing-initial-conversation`, {
    applicantParentOib: applicantParentOib,
    childOib: childOib,
  }) as Promise<InitialConversationQuestionGroup[]>;
};

export const getInitialConversationQuestionnaire = async () => {
  return api.get(
    `${questionnairesUrlPrefix}/initial-conversation`
  ) as Promise<Questionnaire>;
};

export const getNationalities = async () => {
  return api.get(nationalitiesUrlPrefix) as Promise<CodeBook[]>;
};

export const getNationalMinorities = async () => {
  return api.get(nationalMinoritiesUrlPrefix) as Promise<CodeBook[]>;
};

export const getProfessions = async () => {
  return api.get(professionsUrlPrefix) as Promise<CodeBook[]>;
};

export const getProfessionalQualifications = async () => {
  return api.get(professionalQualificationsUrlPrefix) as Promise<CodeBook[]>;
};

export const getParentshipTypes = async () => {
  return api.get(parentshipTypesUrlPrefix) as Promise<CodeBook[]>;
};

export const getParentNumbers = async () => {
  return api.get(parentNumbersUrlPrefix) as Promise<CodeBook[]>;
};

export const getCounties = async () => {
  return api.get(countiesUrlPrefix) as Promise<CodeBook[]>;
};

export const getTowns = async () => {
  return api.get(townsUrlPrefix) as Promise<Town[]>;
};

export const searchTowns = async (countyId: string) => {
  return api.get(`${townsUrlPrefix}`, { CountyId: countyId }) as Promise<
    Town[]
  >;
};

export const getProgramTypes = async () => {
  return api.get(programTypesUrlPrefix) as Promise<CodeBook[]>;
};

export const getPrograms = async () => {
  return api.get(programsUrlPrefix) as Promise<CodeBook[]>;
};

export const getInstitutionPrograms = async (
  pedagogicalYearInstitutionId: number
) => {
  return api.get(
    `${institutionsUrlPrefix}/${pedagogicalYearInstitutionId}/programs`,
    {
      pedagogicalYearInstitutionId: pedagogicalYearInstitutionId,
    }
  ) as Promise<CodeBook[]>;
};

export const getInstitutionProgramsFilter = async (
  pedagogicalYearInstitutionId: number,
  parentIntitutionId?: number
) => {
  return api.get(
    `${institutionsUrlPrefix}/${pedagogicalYearInstitutionId}/programsFilter`,
    {
      pedagogicalYearInstitutionId: pedagogicalYearInstitutionId,
      parentIntitutionId: parentIntitutionId,
    }
  ) as Promise<CodeBook[]>;
};

export const getGroupInstitutions = async (
  pedagogicalYearInstitutionId: number
) => {
  return api.get(
    `${institutionsUrlPrefix}/${pedagogicalYearInstitutionId}/group-institutions`,
    {
      pedagogicalYearInstitutionId: pedagogicalYearInstitutionId,
    }
  ) as Promise<CodeBook[]>;
};

export const getProgramAges = async () => {
  return api.get(programAgesUrlPrefix) as Promise<CodeBook[]>;
};

export const getInstitutionProgramAges = async (
  pedagogicalYearInstitutionId: number
) => {
  return api.get(
    `${institutionsUrlPrefix}/${pedagogicalYearInstitutionId}/program-ages`,
    {
      pedagogicalYearInstitutionId: pedagogicalYearInstitutionId,
    }
  ) as Promise<CodeBook[]>;
};

export const getProgramDurations = async () => {
  return api.get(programDurationsUrlPrefix) as Promise<CodeBook[]>;
};

export const getInstitutionProgramDurations = async (
  pedagogicalYearInstitutionId: number
) => {
  return api.get(
    `${institutionsUrlPrefix}/${pedagogicalYearInstitutionId}/program-durations`,
    {
      pedagogicalYearInstitutionId: pedagogicalYearInstitutionId,
    }
  ) as Promise<CodeBook[]>;
};

export const getRequestMunicipalitiesSearch = async (
  name: string,
  countyId?: number
) => {
  return api.get(`${municipalitiesUrlPrefix}/search`, {
    name: name,
    countyId: countyId,
  }) as Promise<MunicipalityWithCounty[]>;
};

export const getMunicipality = async (municipalityId?: number) => {
  return api.get(
    `${municipalitiesUrlPrefix}/${municipalityId}`
  ) as Promise<MunicipalityWithCounty>;
};

export const getLandingPageMessages = async () => {
  return api.get(`${landingPageMessageUrlPrefix}`) as Promise<
    LandingPageMessage[]
  >;
};

export const searchInstitutions = async (
  request: TableStateWithFilters<InstitutionFilters>
) => {
  return api.get(`${institutionsUrlPrefix}/search`, request) as Promise<
    PagedResult<InstitutionSearchItem>
  >;
};

export const getLocationPrograms = async (
  pedagogicalYearInstitutionId: number,
  parentIntitutionId?: number,
  programAgeId?: number,
  programDurationId?: number,
  programId?: number,
  requestTypeId?: number
) => {
  return api.get(
    `${institutionsUrlPrefix}/${pedagogicalYearInstitutionId}/location-programs`,
    {
      programAgeId: programAgeId,
      parentIntitutionId: parentIntitutionId,
      programDurationId: programDurationId,
      programId: programId,
      requestTypeId: requestTypeId,
    }
  ) as Promise<InstitutionProgramListItem[]>;
};

export const getInstitutionDetails = async (
  pedagogicalYearInstitutionId: number
) => {
  return api.get(
    `${institutionsUrlPrefix}/${pedagogicalYearInstitutionId}/details`
  ) as Promise<InstitutionDetails>;
};

export const getIsGroup = async (pedagogicalYearInstitutionId: number) => {
  return api.get(
    `${institutionsUrlPrefix}/${pedagogicalYearInstitutionId}/isGroup`
  ) as Promise<boolean>;
};

export const getInstitutionCriteria = async (
  pedagogicalYearInstitutionId: number
) => {
  return api.get(
    `${institutionsUrlPrefix}/${pedagogicalYearInstitutionId}/criteria`
  ) as Promise<InstitutionCriterion[]>;
};

export const getEvaluatedCriteria = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/evaluated-criteria`
  ) as Promise<EvaluatedCriterion[]>;
};

export const getDocumentFromOrigin = async (origin: number, fileId: string) => {
  return api.download(
    `${documentsUrlPrefix}/${origin}/${fileId}`
  ) as Promise<any>;
};

export const getApplicantParentDocument = async (fileId: string) => {
  return api.download(
    `${documentsUrlPrefix}/applicant-parent/${fileId}`
  ) as Promise<any>;
};

export const createApplicantParentDocument = async (formData: FormData) => {
  return api.post(
    `${documentsUrlPrefix}/applicant-parent`,
    formData
  ) as Promise<RequestDocumentData[]>;
};

export const createFromExistingApplicantParentDocument = async (
  fileId: string,
  origin: number
) => {
  return api.post(`${documentsUrlPrefix}/applicant-parent/from-existing`, {
    fileId: fileId,
    origin: origin,
  }) as Promise<RequestDocumentData>;
};

export const deleteApplicantParentDocument = async (ids: number[]) => {
  return api.delete(`${documentsUrlPrefix}/applicant-parent`, {
    documentIds: ids,
  }) as Promise<any>;
};

export const getOtherParentDocument = async (fileId: string) => {
  return api.download(
    `${documentsUrlPrefix}/other-parent/${fileId}`
  ) as Promise<any>;
};

export const getUserManual = async () => {
  return api.download(`${documentsUrlPrefix}/user-manual`) as Promise<any>;
};

export const getTermsOfService = async () => {
  return api.download(`${documentsUrlPrefix}/terms-of-service`) as Promise<any>;
};

export const createOtherParentDocument = async (formData: FormData) => {
  return api.post(`${documentsUrlPrefix}/other-parent`, formData) as Promise<
    RequestDocumentData[]
  >;
};

export const createFromExistingOtherParentDocument = async (
  fileId: string,
  origin: number
) => {
  return api.post(`${documentsUrlPrefix}/other-parent/from-existing`, {
    fileId: fileId,
    origin: origin,
  }) as Promise<RequestDocumentData>;
};

export const deleteOtherParentDocument = async (ids: number[]) => {
  return api.delete(`${documentsUrlPrefix}/other-parent`, {
    documentIds: ids,
  }) as Promise<any>;
};

export const getChildDocument = async (fileId: string) => {
  return api.download(`${documentsUrlPrefix}/child/${fileId}`) as Promise<any>;
};

export const createChildDocument = async (formData: FormData) => {
  return api.post(`${documentsUrlPrefix}/child`, formData) as Promise<
    RequestDocumentData[]
  >;
};

export const createFromExistingChildDocument = async (
  fileId: string,
  origin: number
) => {
  return api.post(`${documentsUrlPrefix}/child/from-existing`, {
    fileId: fileId,
    origin: origin,
  }) as Promise<RequestDocumentData>;
};

export const deleteChildDocument = async (ids: number[]) => {
  return api.delete(`${documentsUrlPrefix}/child`, {
    documentIds: ids,
  }) as Promise<any>;
};

export const getCriterionDocument = async (fileId: string) => {
  return api.download(
    `${documentsUrlPrefix}/criterion/${fileId}`
  ) as Promise<any>;
};

export const createCriterionDocument = async (formData: FormData) => {
  return api.post(`${documentsUrlPrefix}/criterion`, formData) as Promise<
    RequestDocumentData[]
  >;
};

export const createFromExistingCriterionDocument = async (
  fileId: string,
  origin: number,
  institutionCriterionId: number
) => {
  return api.post(`${documentsUrlPrefix}/criterion/from-existing`, {
    fileId: fileId,
    origin: origin,
    institutionCriterionId: institutionCriterionId,
  }) as Promise<RequestDocumentData>;
};

export const deleteCriterionDocument = async (ids: number[]) => {
  return api.delete(`${documentsUrlPrefix}/criterion`, {
    documentIds: ids,
  }) as Promise<any>;
};

export const getInitialConversationDocument = async (fileId: string) => {
  return api.download(
    `${documentsUrlPrefix}/initial-conversation/${fileId}`
  ) as Promise<any>;
};

export const createInitialConversationDocument = async (formData: FormData) => {
  return api.post(
    `${documentsUrlPrefix}/initial-conversation`,
    formData
  ) as Promise<RequestDocumentData[]>;
};

export const createFromExistingInitialConversationDocument = async (
  fileId: string,
  origin: number,
  questionGroupQuestionId: number
) => {
  return api.post(`${documentsUrlPrefix}/initial-conversation/from-existing`, {
    fileId: fileId,
    origin: origin,
    questionGroupQuestionId: questionGroupQuestionId,
  }) as Promise<RequestDocumentData>;
};

export const deleteInitialConversationDocument = async (ids: number[]) => {
  return api.delete(`${documentsUrlPrefix}/initial-conversation`, {
    documentIds: ids,
  }) as Promise<any>;
};

export const getPhysicalExamination = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/physical-examination`
  ) as Promise<PhysicalExaminationData>;
};

export const getPhysicalExaminationDocument = async (fileId: string) => {
  return api.download(
    `${documentsUrlPrefix}/physical-examination/${fileId}`
  ) as Promise<any>;
};

export const getIntegrationCriterionData = async (
  criterionFetchingParameters: CriterionFetchingParameters[]
) => {
  return api.post(
    `${criteriaUrlPrefix}/integration-data`,
    criterionFetchingParameters
  ) as Promise<CriterionIntegrationData[]>;
};

export const getMonths = async () => {
  var months = await (api.get(monthsUrlPrefix) as Promise<CodeBook[]>);
  return months;
};

export const getRelationships = async () => {
  return api.get(relationshipsUrlPrefix) as Promise<CodeBook[]>;
};

export const getDocumentTypes = async () => {
  return api.get(documentTypesUrlPrefix) as Promise<CodeBook[]>;
};

export const getReasonNonExistenceOibTypes = async () => {
  return api.get(getReasonNonExistenceOibTypesUrlPrefix) as Promise<CodeBook[]>;
};

export const getSexes = async () => {
  return api.get(sexesUrlPrefix) as Promise<CodeBook[]>;
};

export const getRequestBenefitDataByRequestId = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/benefits-data`
  ) as Promise<RequestBenefitData>;
};

export const getInstitutionBenefits = async (
  pedagogicalYearInstitutionId: number
) => {
  return api.get(
    `${institutionsUrlPrefix}/${pedagogicalYearInstitutionId}/benefits`
  ) as Promise<InstitutionBenefit[]>;
};

export const getBenefitDocument = async (fileId: string) => {
  return api.download(
    `${documentsUrlPrefix}/benefit/${fileId}`
  ) as Promise<any>;
};

export const createBenefitDocument = async (formData: FormData) => {
  return api.post(`${documentsUrlPrefix}/benefit`, formData) as Promise<
    RequestDocumentData[]
  >;
};

export const deleteBenefitDocument = async (ids: number[]) => {
  return api.delete(`${documentsUrlPrefix}/benefit`, {
    documentIds: ids,
  }) as Promise<any>;
};

export const getIntegrationBenefitDetailsData = async (
  benefitDetailsFetchingParameters: BenefitDetailsFetchingParameters
) => {
  return api.post(
    `${benefitsUrlPrefix}/details-integration-data`,
    benefitDetailsFetchingParameters
  ) as Promise<BenefitIntegrationPersonData>;
};

export const getEnrollmentCyclesForPedagogicalYear = async (
  pedagogicalYearId: number
) => {
  var enrollmentCyclesPedagogicalYear = await (api.get(
    `${enrollmentCyclesUrlPrefix}/${pedagogicalYearId}`
  ) as Promise<EnrollmentCyclePedagogicalYear[]>);

  for (var enrollmentCyclePedagogicalYear of enrollmentCyclesPedagogicalYear) {
    if (enrollmentCyclePedagogicalYear.dateFrom) {
      enrollmentCyclePedagogicalYear.dateFrom = new Date(
        enrollmentCyclePedagogicalYear.dateFrom
      );
    }
    if (enrollmentCyclePedagogicalYear.dateTo) {
      enrollmentCyclePedagogicalYear.dateTo = new Date(
        enrollmentCyclePedagogicalYear.dateTo
      );
    }
  }
  return enrollmentCyclesPedagogicalYear;
};

export const login = async (onBehalfRoleId?: number) => {
  const query =
    onBehalfRoleId == null ? "" : `?onBehalfRoleId=${onBehalfRoleId}`;

  return api.get(`${userUrlPrefix}/login${query}`) as Promise<IRoleChangeItem>;
};

export const getRequestHistory = async (requestId: number) => {
  return api.get(`${requestsUrlPrefix}/${requestId}/history`) as Promise<
    RequestHistoryItem[]
  >;
};
